/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useState, useEffect, useCallback } from "react";

// Browser-level ordering for the Home tab's top-level tiles (folders and
// ungrouped services), mirroring useKioskLayout.ts's localStorage approach.
// Unlike Kiosk widgets (only ever added explicitly by the user), Home's
// items are derived live from the services list, so new items can appear
// that aren't in the stored order yet -- getOrderedItems appends those at
// the end automatically.

const STORAGE_KEY = "nyrvex-home-layout";

function loadOrder(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveOrder(order: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
  } catch {
    console.error("Failed to save home layout to localStorage");
  }
}

export interface HomeLayoutItem {
  key: string;
}

export function useHomeLayout() {
  const [order, setOrder] = useState<string[]>(() => loadOrder());

  useEffect(() => {
    saveOrder(order);
  }, [order]);

  // Operates on the caller's *currently displayed* key order (which already
  // includes any items not yet committed to storage) so a drag involving a
  // brand-new item works on the first try instead of needing two drags.
  const reorder = useCallback((currentKeys: string[], activeKey: string, overKey: string) => {
    const from = currentKeys.indexOf(activeKey);
    const to = currentKeys.indexOf(overKey);
    if (from === -1 || to === -1 || from === to) return;
    const next = [...currentKeys];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOrder(next);
  }, []);

  const getOrderedItems = useCallback(
    <T extends HomeLayoutItem>(items: T[]): T[] => {
      const remaining = new Map(items.map((item) => [item.key, item]));
      const ordered: T[] = [];
      for (const key of order) {
        const item = remaining.get(key);
        if (item) {
          ordered.push(item);
          remaining.delete(key);
        }
      }
      ordered.push(...remaining.values());
      return ordered;
    },
    [order],
  );

  return { reorder, getOrderedItems };
}
